import { useToday, addDaysISO } from "@/lib/calendar/today";
import { parseISO, dayOfWeek } from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import type { Holiday } from "@/data/holidays";

export function HomeFaq({
  fallbackISO,
  nextHoliday,
}: {
  fallbackISO: string;
  nextHoliday?: Holiday;
}) {
  const { t, locale } = useLocale();
  const todayClient = useToday();
  const todayISO = todayClient ?? fallbackISO;
  const today = parseISO(todayISO)!;
  const tomorrowISO = addDaysISO(todayISO, 1);
  const tomorrow = parseISO(tomorrowISO)!;

  const todayDow = dayOfWeek(today.y, today.m, today.d);
  const tomorrowDow = dayOfWeek(tomorrow.y, tomorrow.m, tomorrow.d);
  const todayMonth = t.common.months[today.m - 1];
  const tomorrowMonth = t.common.months[tomorrow.m - 1];
  const todayDayName = t.common.days[todayDow];
  const tomorrowDayName = t.common.days[tomorrowDow];

  const items = [
    { q: t.home.faqQ1, a: t.home.faqA1(todayDayName, today.d, todayMonth, today.y) },
    { q: t.home.faqQ2, a: t.home.faqA2(tomorrowDayName, tomorrow.d, tomorrowMonth, tomorrow.y) },
    ...(nextHoliday
      ? (() => {
          const p = parseISO(nextHoliday.date)!;
          return [{
            q: t.home.faqQ3,
            a: t.home.faqA3(translateHolidayName(nextHoliday.name, locale), p.d, t.common.months[p.m - 1], p.y),
          }];
        })()
      : []),
    { q: t.home.faqQ4, a: t.home.faqA4 },
  ];

  return (
    <section className="rounded-lg border border-border bg-card p-5" suppressHydrationWarning>
      <h2 className="mb-3 text-lg font-semibold">{t.home.faqTitle}</h2>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.q}>
            <h3 className="text-sm font-semibold text-foreground">{it.q}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">{it.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}